import { defineComponent } from 'vue'
import type {
  RouteLocationNormalized,
  RouteLocationNormalizedLoaded,
  RouteLocationRaw,
  RouteRecordRaw
} from 'vue-router'
import { createRouter, createWebHistory } from 'vue-router'

import { attendanceRoutes } from '@/modules/attendance/routes'
import { brandsRoutes } from '@/modules/brands/routes.ts'
import { catalogRoutes } from '@/modules/catalog/routes'
import { dailyViewRoutes } from '@/modules/daily-view/routes'
import { deliveryRoutes } from '@/modules/delivery/routes'
import { devicesRoutes } from '@/modules/devices/routes'
import { hardwareStoreRoutes } from '@/modules/hardware-store/routes'
import { integrationsRoutes } from '@/modules/integrations/routes'
import { legalEntitiesRoutes } from '@/modules/legal-entity/routes'
import { locationsRoutes } from '@/modules/locations/routes'
import { loginRoutes } from '@/modules/log-in/routes.ts'
import { newOrganizationRoutes } from '@/modules/new-organization/routes'
import { onboardingRoutes } from '@/modules/onboarding/routes.ts'
import { promotionsRoutes } from '@/modules/promotions/routes'
import { reportsRoutes } from '@/modules/reports/routes'
import { schedulesRoutes } from '@/modules/schedules/routes'
import { setupRoutes } from '@/modules/setup/routes'
import { shopRoutes } from '@/modules/shop/routes'
import { signUpRoutes } from '@/modules/sign-up/routes.ts'
import { subscriptionRoutes } from '@/modules/subscription/routes'
import { tabsReportRoutes } from '@/modules/tabs-report/routes'
import { temporalClosuresRoutes } from '@/modules/temporal-closures/routes'
import { usersRoutes } from '@/modules/users/routes'
import { authGuard, installationGuard, locationTypeGuard, permissionsGuard } from '@/router/guards'
import type { LocationType } from '@/types'

declare module 'vue-router' {
  interface RouteMeta {
    locationTypes?: LocationType[]
    permissions?: string[]
  }
}

const PageNotFound = defineComponent({
  created: function () {
    window.location.href = '/'
  }
})

const routes = [
  {
    path: '/',
    component: () => import('./views/Dashboard.vue'),
    redirect: {
      name: 'home'
    },
    children: [
      ...dailyViewRoutes,
      {
        name: 'config',
        path: '/config',
        component: () => import('./views/Config.vue'),
        meta: {
          locationTypes: ['location', 'singleLocation']
        }
      },
      ...subscriptionRoutes,
      {
        name: 'printers',
        path: '/printers',
        component: () => import('./views/Printers.vue'),
        meta: {
          locationTypes: ['location', 'singleLocation']
        }
      },
      ...catalogRoutes,
      ...deliveryRoutes,
      {
        name: 'floorPlan',
        path: '/floor-plan',
        component: () => import('@/modules/floorplan/views/Floorplan.vue'),
        meta: {
          locationTypes: ['location', 'singleLocation']
        }
      },
      ...devicesRoutes,
      ...integrationsRoutes,
      {
        name: 'employees',
        path: '/employees',
        component: () => import('@/modules/employees/views/Employees.vue'),
        meta: {
          locationTypes: ['location', 'singleLocation']
        }
      },
      {
        name: 'posConfig',
        path: '/pos-config',
        component: () => import('@/views/PosConfig.vue'),
        meta: {
          locationTypes: ['location', 'singleLocation']
        }
      },
      {
        name: 'fastModeConfig',
        path: '/fast-mode-config',
        component: () => import('@/components/FastModeConfig.vue'),
        meta: {
          locationTypes: ['location', 'singleLocation']
        }
      },
      ...legalEntitiesRoutes,
      {
        name: 'cardRequest',
        path: '/legal-entities/:legalEntityId/card-request',
        component: () => import('@/components/CardRequest.vue'),
        meta: {
          locationTypes: ['organization', 'singleLocation', 'location']
        }
      },
      {
        name: 'invoice',
        path: '/invoices/:id',
        component: () => import('@/modules/billing/views/Invoice.vue'),
        meta: {
          locationTypes: ['organization', 'singleLocation', 'location']
        }
      },
      ...brandsRoutes,
      ...locationsRoutes,
      {
        name: 'customers',
        path: '/customers',
        component: () => import('@/views/Customers.vue'),
        meta: {
          locationTypes: ['organization', 'singleLocation']
        }
      },
      {
        name: 'customer',
        path: '/customers/:customerId',
        component: () => import('@/views/Customer.vue'),
        meta: {
          locationTypes: ['organization', 'singleLocation']
        }
      },
      ...usersRoutes,
      ...promotionsRoutes,
      {
        name: 'movementsList',
        path: '/movements',
        component: () => import('@/modules/billing/views/Movements.vue'),
        meta: {
          locationTypes: ['location', 'singleLocation']
        }
      },
      {
        name: 'payoutsList',
        path: '/payouts',
        component: () => import('@/modules/billing/views/Payouts.vue'),
        meta: {
          locationTypes: ['location', 'singleLocation']
        }
      },
      {
        name: 'payout',
        path: '/payouts/:id',
        component: () => import('@/modules/billing/views/Payout.vue'),
        meta: {
          locationTypes: ['location', 'singleLocation']
        }
      },
      ...shopRoutes,
      {
        name: 'QR',
        path: '/location/QR',
        component: () => import('@/modules/qr/views/QR.vue'),
        meta: {
          locationTypes: ['location', 'singleLocation']
        }
      },
      {
        name: 'organizationBookings',
        path: '/organization/bookings',
        component: () => import('@/modules/bookings/view/OrganizationConfig.vue'),
        meta: {
          locationTypes: ['organization']
        }
      },
      {
        name: 'singleLocationBookings',
        path: '/scripts',
        component: () => import('@/modules/bookings/view/OrganizationConfig.vue'),
        meta: {
          locationTypes: ['singleLocation']
        }
      },
      ...schedulesRoutes,
      ...temporalClosuresRoutes,
      ...hardwareStoreRoutes,
      {
        path: '/location/bookings',
        children: [
          {
            name: 'bookingsConfig',
            path: 'config',
            component: () => import('@/components/BookingsConfig.vue'),
            meta: {
              locationTypes: ['location', 'singleLocation']
            }
          },
          {
            name: 'bookingsManager',
            path: 'manager',
            component: () => import('@/modules/bookings/view/Manager.vue'),
            meta: {
              locationTypes: ['location', 'singleLocation']
            }
          },
          {
            name: 'bookingsCommunications',
            path: 'communications',
            component: () => import('@/modules/bookings/view/Communications.vue'),
            meta: {
              locationTypes: ['location', 'singleLocation']
            }
          },
          {
            name: 'bookingsAvailability',
            path: 'availability',
            component: () => import('@/modules/bookings/view/Availability.vue'),
            meta: {
              locationTypes: ['location', 'singleLocation']
            }
          },
          {
            name: 'bookingsPolicy',
            path: 'policies',
            component: () => import('@/modules/bookings/view/Policies.vue'),
            meta: {
              locationTypes: ['location', 'singleLocation']
            }
          },
          {
            name: 'bookingsShifts',
            path: 'shifts',
            component: () => import('@/modules/bookings/view/Shifts.vue'),
            meta: {
              locationTypes: ['location', 'singleLocation']
            }
          }
        ]
      },
      {
        name: 'lastProducts',
        path: '/last-products',
        component: () => import('@/modules/last-products/views/LastProducts.vue'),
        meta: {
          locationTypes: ['location', 'singleLocation']
        }
      },
      ...attendanceRoutes,
      ...tabsReportRoutes,
      ...reportsRoutes
    ]
  },
  ...setupRoutes,
  ...newOrganizationRoutes,
  ...loginRoutes,
  {
    name: 'supportLogin',
    path: '/support/login',
    component: () => import('@/components/SupportLogin.vue')
  },
  {
    name: 'redirectLogin',
    path: '/redirect/login',
    component: () => import('@/components/RedirectLogin.vue')
  },
  ...signUpRoutes,
  ...onboardingRoutes,
  {
    name: 'newPassword',
    path: '/new-password/:recoverId?/:token?',
    component: () => import('./components/NewPassword.vue')
  },
  {
    name: 'rating',
    path: '/rating/:tabId/vote/:rate',
    component: () => import('@/components/Rating.vue')
  },
  {
    name: 'externalOAuth',
    path: '/external-oauth/:integration',
    component: () => import('@/components/ExternalOAuth.vue')
  },
  { path: '/:notvalid(.*)', component: PageNotFound }
] as RouteRecordRaw[]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, _from, savedPosition) {
    return new Promise(resolve => {
      if (savedPosition) {
        resolve(savedPosition)
      } else if (to.hash) {
        setTimeout(() => {
          const element = document.querySelector(to.hash)
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' })
            resolve({ el: to.hash })
          } else {
            resolve({ top: 0 })
          }
        }, 250)
      } else {
        resolve({ top: 0 })
      }
    })
  }
})

router.beforeEach(
  (
    to: RouteLocationNormalized,
    from: RouteLocationNormalizedLoaded
  ): boolean | RouteLocationRaw => {
    const auth = authGuard(to)
    if (auth !== true) return auth

    const installation = installationGuard(to)
    if (installation !== true) return installation

    const locationType = locationTypeGuard(to)
    if (locationType !== true) return locationType

    const permissions = permissionsGuard(to, from)
    if (permissions !== true) return permissions

    return true
  }
)

export default router
